import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Checkbox } from 'ui-components/components/checkbox';
import { useEnableLotTracking } from '../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { useUserDateFormat } from '../../../../../hooks/use-datetime-formats';
import { openProductDetailModal } from '../../../../../store/products/products.actions';
import PurchaseOrderProgressBar from '../purchase-order-progress-bar';
import { getReceivedProductColumns } from './products/received-products-columns';
import { useReceivedProducts } from './purchase-order-receiving-context';
import { messages } from './received-products.messages';
import styles from './received-products.styles.module.less';
import { useIntl } from 'react-intl';

export const ReceivedProducts = ({ warehouseLocations }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const { userDateFormat } = useUserDateFormat();
  const {
    receiveFormData,
    updateData,
    dynamicReceived,
    dynamicRejected,
    totals,
    isLoading,
    showAdvancedColumns,
    toggleAdvancedColumns,
    highlightedProductId,
  } = useReceivedProducts();

  useEffect(() => {
    if (!highlightedProductId) return;

    const row = document.querySelector(`.row-product-${highlightedProductId}`);
    if (row) {
      row.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }
  }, [highlightedProductId]);

  const openProductDetails = useCallback(
    (productId) => {
      if (!productId) return;
      dispatch(openProductDetailModal(productId));
    },
    [dispatch],
  );

  const isLotTrackingEnabled = useEnableLotTracking();

  const columns = useMemo(
    () => getReceivedProductColumns(updateData, warehouseLocations, openProductDetails, formatMessage, showAdvancedColumns, userDateFormat),
    [updateData, warehouseLocations, openProductDetails, formatMessage, showAdvancedColumns, userDateFormat],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: receiveFormData,
    },
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  const getBodyRowClassName = useCallback(
    (row) => {
      const baseClass = row.original.productId === highlightedProductId ? styles['highlight-row'] : '';
      return `${baseClass} row-product-${row.original.productId}`;
    },
    [highlightedProductId],
  );

  return (
    <FlexColumn className={styles['receive-grid']} gap={Gap.Space16}>
      {isLoading || !warehouseLocations?.length ? (
        <LoadingIndicator />
      ) : (
        <>
          <div className={styles['divider-line']} />
          <div className={styles['header-row']}>
            <PurchaseOrderProgressBar receivedItems={dynamicReceived} rejectedItems={dynamicRejected} totalItems={totals.ordered} />
          </div>
          {isLotTrackingEnabled && (
            <div className={styles['checkbox-container']}>
              <Checkbox checked={showAdvancedColumns} label={formatMessage(messages.showAdvancedFields)} onChange={toggleAdvancedColumns} />
            </div>
          )}
          <div className={styles['table-container']}>
            <ReactTableWrapper
              key={showAdvancedColumns ? 'advanced' : 'basic'}
              className={styles.table}
              getBodyRowClassName={getBodyRowClassName}
              getTableBodyProps={getTableBodyProps}
              getTableProps={getTableProps}
              headerGroups={headerGroups}
              memoizeRows
              prepareRow={prepareRow}
              rows={rows}
            />
          </div>
        </>
      )}
    </FlexColumn>
  );
};
